import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateCompanyModalComponent } from '@app/shared/modals/create-company-modal/create-company-modal.component';
import { CreateProductModalComponent } from '@app/shared/modals/create-product-modal/create-product-modal.component';
import { Checklist } from '@models/checklist.model';
import { Company } from '@models/company.model';
import { LineItem } from '@models/line-item.model';
import { Product } from '@models/product.model';
import { User } from '@models/user.model';
import { PurchaseOrderDto } from '@app/models/purchase-order.model.dto';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyService } from '@services/company.service';
import { AppPurchaseOrderService } from '@services/purchase-order.service';
import { SharedService } from '@services/shared.service';
import { Toaster } from '@services/toaster.service';
import * as moment from 'moment';
import { LocalStorage } from 'ngx-webstorage';
import { of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { environment } from '@env/environment';

@Component({
  selector: 'ngx-po-info-step',
  templateUrl: './po-info-step.component.html',
  styleUrls: ['./po-info-step.component.scss'],
})
export class PoInfoStepComponent implements OnInit {
  isRCDSS = environment.name === 'rcdss';
  @LocalStorage() organizationId: string;
  @Input() parentForm: FormGroup;
  @Input() purchaseOrder: PurchaseOrderDto;
  @Input() suppliers: Company[];
  @Input() checklist: Checklist;
  @Input() showStepsButtons: boolean = true;

  @Output() purchaseOrderChange: EventEmitter<PurchaseOrderDto> = new EventEmitter<PurchaseOrderDto>();
  @Output() checklistChange: EventEmitter<Checklist> = new EventEmitter<Checklist>();
  @Output() saveDraft: EventEmitter<void> = new EventEmitter<void>();
  @Output() discard: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('lineItemsForm', { static: true }) lineItemsForm: ElementRef;

  poInfoForm: FormGroup;
  poLineItemsForm: FormGroup;
  frequencies = this.sharedService.frequencies();
  priorities = this.sharedService.priorities();
  lineItems: LineItem[] = [];
  products: Product[] = [];
  availableProducts: Product[] = [];

  _canSupplierReceivePos: boolean;
  @Output() canSupplierReceivePos: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private formBuilder: FormBuilder,
              private sharedService: SharedService,
              private $company: CompanyService,
              private purchaseOrderService: AppPurchaseOrderService,
              private $toaster: Toaster,
              private $modal: NgbModal,
              private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  ngOnInit() {
    this.poInfoForm = (this.parentForm.get('poInfo') as FormGroup);

    this.poInfoForm.get('customer_po_number').setAsyncValidators(this.validatePoNumber.bind(this));
    this.poInfoForm.get('sales_order_number').setAsyncValidators(this.validateSalesOrderNumber.bind(this));

    this.poLineItemsForm = this.formBuilder.group({
      product_id: [null, [Validators.required]],
      quantity_ordered: [null, [Validators.min(1)]],
    });

    this.canSupplierReceivePos.subscribe(val => this._canSupplierReceivePos = val);
    this.canSupplierReceivePos.emit(true);


    this.poInfoForm.get('supplier_id').valueChanges
      .pipe(filter(value => !!value))
      .subscribe((value) => {
        const selectedSupplier = this.suppliers.find(p => p.id === value);
        if (selectedSupplier) {
          this.canSupplierReceivePos.emit(selectedSupplier.can_receive_pos);
        }
        this.listSupplierProducts();
      });

    this.poLineItemsForm.get('product_id').valueChanges.subscribe((value) => {
      if (value === 'new') {
        this.poLineItemsForm.get('product_id').reset();
        return;
      }
    });
    this.lineItems = this.poInfoForm.get('line_items').value;
    this.poInfoForm.get('line_items').valueChanges.subscribe((lineItems) => {
      this.lineItems = lineItems;
      if (this.poInfoForm.valid) this.savePODraft();
    });

    this.poInfoForm.get('finished_goods_reports_period').valueChanges.subscribe((period) => {
      if (period === 'No report needed' && !this.poInfoForm.get('report_start_date').value) {
        this.poInfoForm.get('report_start_date').setValue(this.poInfoForm.get('order_date').value);
      }
    });

    this.poInfoForm.get('order_date').valueChanges.subscribe((date) => {
      if (this.poInfoForm.get('finished_goods_reports_period').value === 'No report needed') {
        this.poInfoForm.get('report_start_date').setValue(date);
      }
    });

    this.poInfoForm.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
      )
      .subscribe((data) => {
        if (this.poInfoForm.valid) this.savePODraft();
      });

    if (this.purchaseOrder) {
      if (this.purchaseOrder.line_items) {
        this.lineItems = this.purchaseOrder.line_items;
      }
      if (this.purchaseOrder.supplier_id) {
        const selectedSupplier = this.suppliers.find(p => p.id === this.purchaseOrder.supplier_id);
        if (selectedSupplier) {
          this.canSupplierReceivePos.emit(selectedSupplier.can_receive_pos);
        }
        this.listSupplierProducts();
      }
      if (moment.utc(this.purchaseOrder.cancel_date).year() === 1900) {
        this.poInfoForm.get('cancel_date').setValue('');
        this.poInfoForm.controls.cancel_date.markAsTouched();
        this.changeDetectorRef.detectChanges();
      }
      if (moment.utc(this.purchaseOrder.ship_date).year() === 1900) {
        this.poInfoForm.get('ship_date').setValue('');
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  searchProduct(term, item) {
    const searchTerm = term.toLowerCase();
    return item.name.toLowerCase().indexOf(searchTerm) >= 0 ||
      (item.customer_part_id && item.customer_part_id.toLowerCase().includes(searchTerm));
  }

  validatePoNumber(control: AbstractControl) {
    if (this.purchaseOrder) {
      if (this.purchaseOrder.customer_po_number === control.value) {
        return of(null);
      }
    }

    return this.$company.isPurchaseOrderNumberAvailable(this.organizationId, control.value)
      .pipe(
        catchError((e) => {
          return of({
            exists: true,
          });
        })).pipe(map(result => {
          if (result) {
            return result;
          }
          return null;
        },
      ));
  }

  validateSalesOrderNumber(control: AbstractControl) {
    if (this.purchaseOrder) {
      if (this.purchaseOrder.sales_order_number === control.value) {
        return of(null);
      }
    }

    return this.$company.isSalesOrderNumberAvailable(this.organizationId, control.value)
      .pipe(
        catchError((e) => {
          return of({
            exists: true,
          });
        })).pipe(map(result => {
          if (result) {
            return result;
          }
          return null;
        },
      ));
  }

  getReportPeriod(period) {
    const { report_start_date: reportDate, cancel_date: cancelDate } = this.poInfoForm.getRawValue();
    const monthsCount = moment(cancelDate).diff(reportDate, 'months');
    const weeksCount = moment(cancelDate).diff(reportDate, 'weeks');

    if (period === 'weekly' &&
      (moment(cancelDate).toDate().getTime() - moment(reportDate).toDate().getTime()) < 604800000) {
      const errMessage = `You chose weekly reporting, but there is less than a
                                    week between report and cancel dates.`;
      this.$toaster.show('warning', 'Incomplete form', errMessage);
      this.poInfoForm.get('finished_goods_reports_period').reset();
    }
    if (period === 'monthly' && monthsCount < 1) {
      const errMessage = `You chose monthly reporting, but there is less than a
                                    month between report and cancel dates.`;
      this.$toaster.show('warning', 'Incomplete form', errMessage);
      this.poInfoForm.get('finished_goods_reports_period').reset();
    }
    if (period === 'biweekly' && weeksCount < 2) {
      const errMessage = `You chose biweekly reporting, but there is less than two
                                    weeks between report and cancel dates.`;
      this.$toaster.show('warning', 'Incomplete form', errMessage);
      this.poInfoForm.get('finished_goods_reports_period').reset();
    }
  }

  // when a customer selects a supplier the product list will be populated using the products added by the supplier
  listSupplierProducts() {
    this.$company.getProductsByCompanyId(this.poInfoForm.get('supplier_id').value)
      .subscribe((products) => {
        this.products = products;
        this.availableProducts = this.products.filter((product) => {
          return !this.lineItems.find(li => li.product_id === product.id);
        });

        const placeholderProduct = this.availableProducts.find(p => p.name === 'Placeholder Product');
        if (placeholderProduct && (!this.purchaseOrder || this.purchaseOrder.status === 'qb-imported')) {
          this.poLineItemsForm.get('product_id').setValue(placeholderProduct.id);
          this.poLineItemsForm.get('quantity_ordered').setValue(10);
          this.addLineItem();
        }
      }, (error) => {
        const errMsg = error.error && error.error.detail ? error.error.detail : error.message;
        this.$toaster.show('error', 'Products', errMsg);
      });
  }

  getCheckList() {
    this.$company
      .getCheckList(this.organizationId, this.purchaseOrder.id)
      .subscribe((checklist) => {
        this.checklist = checklist[0];
        this.checklistChange.emit(this.checklist);
        this.changeDetectorRef.detectChanges();
      });
  }

  addLineItem() {
    if (this.poLineItemsForm.invalid) {
      if (!this.poLineItemsForm.dirty) {
        this.lineItemsForm.nativeElement.reset();
      }
      return;
    }
    const { product_id: selectedProductId, quantity_ordered } = this.poLineItemsForm.getRawValue();
    // check if the product already exists within the array
    const lineItemsContainsProduct = this.lineItems.some(l => l.product_id === selectedProductId);
    if (lineItemsContainsProduct) {
      this.$toaster.show('error', 'Error', 'Product already added');
      this.poLineItemsForm.reset();
      this.lineItemsForm.nativeElement.reset();
      return;
    }

    // it shouldn't allow inserting empty line items
    if (!selectedProductId) return;

    const selectedProduct = this.products.find(p => p.id === selectedProductId);

    const lineItemToBeAdded: LineItem = LineItem.fromProduct(selectedProduct, quantity_ordered);

    if (this.purchaseOrder && this.purchaseOrder.id) {
      lineItemToBeAdded.setPurchaseOrder(this.purchaseOrder);
    }

    if (!lineItemToBeAdded.cancel_date) {
      lineItemToBeAdded.cancel_date = '1900-01-01T00:00:00Z';
    }

    this.lineItems = [
      ...this.lineItems,
      lineItemToBeAdded,
    ];

    this.poLineItemsForm.reset();
    this.lineItemsForm.nativeElement.reset();
    this.resetLineItemsForm();
    this.listSupplierProducts();
    this.savePODraft();
    return;
  }

  removeLineItem($index) {
    this.lineItems = [
      ...this.lineItems.slice(0, $index),
      ...this.lineItems.slice($index + 1),
    ];
    this.savePODraft();
    this.listSupplierProducts();
  }


  updateLineItem($event) {
    this.savePODraft();
  }

  addNewCompany() {
    const modalRef = this.$modal.open(CreateCompanyModalComponent, {
      size: 'lg',
      container: 'nb-layout',
      keyboard: false,
      windowClass: 'company-modal',
    });
    modalRef.componentInstance.company_as = 'supplier';
    modalRef.result
      .then(async (result) => {
        if (result) {
          return this.$company.suppliers(this.organizationId).toPromise()
            .then((suppliers) => {
              this.suppliers = suppliers;
              return result;
            });
        }
        return result;
      })
      .then((result: any) => {
        if (result) {
          this.poInfoForm.get('supplier_id').setValue(result.id);
          this.listSupplierProducts();
          this.changeDetectorRef.detectChanges();
        }
      }, (error) => {
        if (error) {
          const errMsg = error.error && error.error.detail ? error.error.detail : error.message;
          this.$toaster.show('error', 'Company', errMsg);
        }
      });
  }

  addProduct() {
    const activeModal = this.$modal.open(CreateProductModalComponent, {
      size: 'lg',
      container: 'nb-layout',
      keyboard: false,
    });
    const supplierId = this.poInfoForm.get('supplier_id').value;
    activeModal.componentInstance.role = 'customer';
    activeModal.componentInstance.idFromPO = supplierId;

    activeModal.result.then(result => {
      if (result && result[0] === 'success') {
        this.listSupplierProducts();
        this.poLineItemsForm.get('product_id').setValue(result[1].id);
        this.changeDetectorRef.detectChanges();
      }
    }, (error) => {
      if (error) {
        const errMsg = error.error && error.error.detail ? error.error.detail : error.message;
        this.$toaster.show('error', 'Product', errMsg);
      }
    });
  }

  savePODraft() {
    if (this.poInfoForm.invalid) return;
    const poData = this.poInfoForm.getRawValue();
    poData.customer_id = this.organizationId;
    poData.order_date = moment.utc(poData.order_date).format();
    poData.line_items = this.lineItems.map((liteItem: LineItem) => {
      liteItem.order_date = poData.order_date;
      liteItem.ship_date = poData.ship_date;
      liteItem.cancel_date = poData.cancel_date;
      return liteItem;
    });

    poData.ship_date = moment.utc(poData.ship_date).format();
    poData.cancel_date = moment.utc(poData.cancel_date).format();

    const purchaseOrder = {
      ...this.purchaseOrder,
      ...poData,
    };

    this.purchaseOrderService.saveDraft(this.organizationId, purchaseOrder, false, false)
      .subscribe((result) => {
        this.purchaseOrder = result;
        this.lineItems = result.line_items || [];
        this.purchaseOrderChange.emit(result);
        this.getCheckList();
      });
  }

  shouldHighlightCancelDate() {
    return this.purchaseOrder?.id && moment.utc(this.purchaseOrder.cancel_date).year() === 1900;
  }

  resetLineItemsForm() {
    this.poLineItemsForm.reset();
    this.lineItemsForm.nativeElement.reset();
  }
}
